import React, {useState, useRef, useEffect, useMemo} from 'react';
import {Box} from '@mui/material';
import {t} from 'i18next';
import AurDateSpan from './AurDateSpan';
import AurDatePopper from './AurDatePopper';
import {isValidDate, isStartDateSmaller} from '../../../utils/utils';
import {AurTextField} from '../aur-text-field/AurTextField';
import AurButton from '../aur-button/AurButton';

interface DateRangePickerProps {
  startYearFromRange?: number;
  onDateChange?: (startDate: string, endDate: string) => void;
  initialStartDate?: {year: string; month: string; day: string};
  initialEndDate?: {year: string; month: string; day: string};
  shouldShowApplyButton?: boolean;
  isLoading?: boolean;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startYearFromRange = 100,
  onDateChange,
  initialStartDate = {year: 'YYYY', month: 'MM', day: 'DD'},
  initialEndDate = {year: 'YYYY', month: 'MM', day: 'DD'},
  shouldShowApplyButton = false,
  isLoading = false,
}) => {
  const currentYear = new Date().getFullYear();
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [isApplyButtonEnabled, setIsApplyButtonEnabled] = useState(false);
  const dropDownCloseTimeout = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const preventDropDownClose = useRef<boolean>(false);

  useEffect(() => {
    const isStartDateChanged =
      startDate.year !== initialStartDate.year ||
      startDate.month !== initialStartDate.month ||
      startDate.day !== initialStartDate.day;

    const isEndDateChanged =
      endDate.year !== initialEndDate.year ||
      endDate.month !== initialEndDate.month ||
      endDate.day !== initialEndDate.day;
    setIsApplyButtonEnabled(isStartDateChanged || isEndDateChanged);
  }, [startDate, endDate, initialStartDate, initialEndDate]);

  const [openType, setOpenType] = useState<
    | 'startYear'
    | 'startMonth'
    | 'startDay'
    | 'endYear'
    | 'endMonth'
    | 'endDay'
    | null
  >(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isValid, setValid] = useState<boolean>(false);
  const [errorTextDate, setErrorTextDateRange] = useState<string>('');

  useEffect(() => {
    setStartDate(initialStartDate);
  }, [initialStartDate]);

  useEffect(() => {
    setEndDate(initialEndDate);
  }, [initialEndDate]);

  const textFieldRef = useRef<HTMLInputElement | null>(null);

  const months = useMemo(
    () =>
      Array.from({length: 12}, (_, i) => ({
        key: String(i + 1).padStart(2, '0'),
        value: new Date(0, i).toLocaleString('default', {month: 'long'}),
      })),
    []
  );

  const days = useMemo(
    () =>
      Array.from({length: 31}, (_, i) => {
        const day = String(i + 1).padStart(2, '0');
        return {key: day, value: day};
      }),
    []
  );

  const years = useMemo(
    () =>
      Array.from({length: startYearFromRange}, (_, i) => {
        const year = String(currentYear - i);
        return {key: year, value: year};
      }),
    [currentYear]
  );

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month, 0).getDate();
  };

  const handleDateChange = (type: string, value: string) => {
    if (type.startsWith('start')) {
      setStartDate((prev) => {
        const newDate = {...prev, [type.slice(5).toLowerCase()]: value};
        if (type === 'startYear' || type === 'startMonth') {
          const year = parseInt(newDate.year, 10);
          const month = parseInt(newDate.month, 10);
          const validDays = getDaysInMonth(year, month);
          if (parseInt(newDate.day, 10) > validDays) {
            newDate.day = String(validDays).padStart(2, '0');
          }
        }
        return newDate;
      });
    } else {
      setEndDate((prev) => {
        const newDate = {...prev, [type.slice(3).toLowerCase()]: value};
        if (type === 'endYear' || type === 'endMonth') {
          const year = parseInt(newDate.year, 10);
          const month = parseInt(newDate.month, 10);
          const validDays = getDaysInMonth(year, month);
          if (parseInt(newDate.day, 10) > validDays) {
            newDate.day = String(validDays).padStart(2, '0');
          }
        }
        return newDate;
      });
    }
    setOpenType(null);
    setAnchorEl(null);
  };

  useEffect(() => {
    checkIfDateValid();
  }, [startDate, endDate]);
  const invalidPatterns = /YYYY|MM|DD|0000|\/00/;

  const validYears = years.map((year) => year.value);

  const checkIfDateValid = () => {
    const startDateString = `${startDate.year}/${startDate.month}/${startDate.day}`;
    const endDateString = `${endDate.year}/${endDate.month}/${endDate.day}`;
    const charactersToCheck = ['Y', 'M', 'D'];
    if (
      !(
        charactersToCheck.some((char) => startDateString.includes(char)) ||
        charactersToCheck.some((char) => endDateString.includes(char)) ||
        invalidPatterns.test(startDateString) ||
        invalidPatterns.test(endDateString)
      )
    ) {
      if (!isValidDate(startDateString)) {
        setValid(false);
        setErrorTextDateRange(t('dateRangePicker.error.invalidDate'));
      } else if (!isValidDate(endDateString)) {
        setValid(false);
        setErrorTextDateRange(t('dateRangePicker.error.invalidDate'));
      } else if (!isStartDateSmaller(startDate, endDate)) {
        setValid(false);
        setErrorTextDateRange(t('dateRangePicker.error.invalidStart'));
      } else if (
        !validYears.includes(startDate.year) ||
        !validYears.includes(endDate.year)
      ) {
        setValid(false);
        setErrorTextDateRange(t('dateRangePicker.error.invalidDate'));
      } else {
        setValid(true);
        setErrorTextDateRange('');
      }
    } else {
      setValid(true);
      setErrorTextDateRange('');
    }
  };

  const areDatesValid = () => {
    let valid = false;
    const startDateString = `${startDate.year}/${startDate.month}/${startDate.day}`;
    const endDateString = `${endDate.year}/${endDate.month}/${endDate.day}`;
    if (!isValidDate(startDateString)) {
      valid = false;
    } else if (!isValidDate(endDateString)) {
      valid = false;
    } else if (!isStartDateSmaller(startDate, endDate)) {
      valid = false;
    } else if (
      !validYears.includes(startDate.year) ||
      !validYears.includes(endDate.year)
    ) {
      valid = false;
    } else {
      valid = true;
    }
    return valid;
  };

  const handleClick = (
    type:
      | 'startYear'
      | 'startMonth'
      | 'startDay'
      | 'endYear'
      | 'endMonth'
      | 'endDay',
    e: React.MouseEvent<HTMLElement> | React.FocusEvent<HTMLInputElement>,
    ref: React.RefObject<HTMLInputElement>
  ) => {
    /**
     * Preventing drop down close event because when dropdown is already open
     * and user click on other date then first we get open dropdown event here and close
     * dropdown event also execute after that so user need to click two time to open dropdown
     */
    if (e.type === 'click' && openType !== type)
      preventDropDownClose.current = true;

    /**
     * Cancel dropdown close event by clear timeout because when user switch between date input using
     * keyboard tab then first focused input's blur event get called and next input's focus event trigger open
     * dropdown event so sometime this sequance misbehave and getting unwanted result. To prevent this we are dealying dropdown close
     */
    if (dropDownCloseTimeout.current) {
      clearTimeout(dropDownCloseTimeout.current);
      dropDownCloseTimeout.current = null;
    }
    setOpenType(type);
    setAnchorEl(ref.current);
  };

  const handleClose = () => {
    if (preventDropDownClose.current === false) {
      /**
       * Dealying dropdwon close by 250ms to prevent conflict of multiple event of open and close
       * dropdown at same time by switching input using keyboard tab
       */
      dropDownCloseTimeout.current = setTimeout(() => {
        setOpenType(null);
        setAnchorEl(null);
      }, 250);
    }
    preventDropDownClose.current = false;
  };

  const handleBlur = () => {
    handleClose();
  };

  // Determine which value to pass as selectedValue based on openType
  const selectedValue = openType?.startsWith('start')
    ? startDate[openType.slice(5).toLowerCase() as keyof typeof startDate]
    : openType?.startsWith('end')
      ? endDate[openType.slice(3).toLowerCase() as keyof typeof endDate]
      : '';

  return (
    <Box sx={{width: 342}} ref={textFieldRef}>
      <AurTextField
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            border: '1px solid #4C6181',
            '&:hover fieldset': {
              borderColor: '#5265FF',
              borderWidth: '2px',
            },
          },
        }}
        helperText={isValid ? '' : errorTextDate}
        value=""
        fullWidth
        error={Boolean(errorTextDate)}
        ariaLabel={t('dateRangePicker.datePicker')}
        InputProps={{
          readOnly: true,
          startAdornment: (
            <div
              className="main-input-div"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <div>
                <div className={`input-label ${!isValid ? 'error-color' : ''}`}>
                {t('dateRangePicker.datePosted')}:
                </div>
                <div className="date-div">
                  <AurDateSpan
                    value={startDate.year}
                    type="startYear"
                    onClick={handleClick}
                    onChange={handleDateChange}
                    onBlur={handleBlur}
                    textFieldRef={textFieldRef}
                    className="date-span"
                    style={{width: '46px'}}
                  />
                  <span>/</span>
                  <AurDateSpan
                    value={startDate.month}
                    type="startMonth"
                    onClick={handleClick}
                    onChange={handleDateChange}
                    onBlur={handleBlur}
                    textFieldRef={textFieldRef}
                    className="date-span"
                    style={{width: '30px'}}
                  />
                  <span>/</span>
                  <AurDateSpan
                    value={startDate.day}
                    type="startDay"
                    onClick={handleClick}
                    onChange={handleDateChange}
                    onBlur={handleBlur}
                    textFieldRef={textFieldRef}
                    className="date-span"
                    style={{width: '26px'}}
                  />
                  <span
                    style={{
                      margin: '0 3px 15px 3px',
                    }}
                  >
                    {' '}
                    __{' '}
                  </span>
                  <AurDateSpan
                    value={endDate.year}
                    type="endYear"
                    onClick={handleClick}
                    onChange={handleDateChange}
                    onBlur={handleBlur}
                    textFieldRef={textFieldRef}
                    className="date-span"
                    style={{width: '46px'}}
                  />
                  <span>/</span>
                  <AurDateSpan
                    value={endDate.month}
                    type="endMonth"
                    onClick={handleClick}
                    onChange={handleDateChange}
                    onBlur={handleBlur}
                    textFieldRef={textFieldRef}
                    className="date-span"
                    style={{width: '30px'}}
                  />
                  <span>/</span>
                  <AurDateSpan
                    value={endDate.day}
                    type="endDay"
                    onClick={handleClick}
                    onChange={handleDateChange}
                    onBlur={handleBlur}
                    textFieldRef={textFieldRef}
                    className="date-span"
                    style={{width: '26px'}}
                  />
                </div>
              </div>
              {shouldShowApplyButton ? (
                <div>
                  {' '}
                  <AurButton
                    onClick={() => {
                      onDateChange &&
                        onDateChange(
                          `${startDate.year}/${startDate.month}/${startDate.day}`,
                          `${endDate.year}/${endDate.month}/${endDate.day}`
                        );
                    }}
                    disabled={!areDatesValid() || !isApplyButtonEnabled}
                    classNames="apply-button"
                    type="continue"
                    size="Medium"
                    label={t('searchableDropdown.apply')}
                    dataTestId='apply-button'
                    showLoader={isLoading}
                  >
                    {' '}
                  </AurButton>{' '}
                </div>
              ) : (
                ''
              )}
            </div>
          ),
        }}
      />

      <AurDatePopper
        open={Boolean(openType)}
        anchorEl={anchorEl}
        handleClose={handleClose}
        items={
          openType?.includes('Month')
            ? months
            : openType?.includes('Day')
              ? days
              : years
        }
        handleItemClick={(value) => handleDateChange(openType as string, value)}
        selectedValue={selectedValue}
        isValid={isValid}
        errorTextDate={errorTextDate}
      />
    </Box>
  );
};

export default DateRangePicker;
